/**
 * CAT Data Processor
 * -----------------
 * Auteur       : Janfix - Jean-Philippe Rivière
 * Projet       : WASPL – Plateforme d'évaluation numérique
 * Description  : Utilitaire pour traiter et formater les données CAT pour la base de données
 * Licence      : AGPL-3.0 pour la communauté des développeurs et usage non commercial
 *                Propriétaire pour les utilisations professionnelles et commerciales
 * Version      : 1.0.0
 * Date         : 2025
 *
 * 📌 Fonctions utilitaires pour transformer les données CAT engine vers le format MongoDB
 */

/**
 * Construit l'objet catData selon le modèle MongoDB
 * @param {Object} catEngine - Instance du moteur CAT
 * @param {Object} responsesStore - Store des réponses de l'élève
 * @returns {Object} Objet catData formaté pour MongoDB
 */
export function buildCATData(catEngine, responsesStore) {
  if (!catEngine) {
    throw new Error('CAT Engine is required to build CAT data');
  }

  const finalReport = catEngine.generateFinalReport();
  const currentState = catEngine.getCurrentState();
  
  // Calculer les niveaux min/max atteints
  const levelProgression = catEngine.questionHistory.map(entry => entry.level);
  const maxLevelReached = Math.max(...levelProgression);
  const minLevelReached = Math.min(...levelProgression);
  
  // Calculer la durée totale du test
  const questionHistory = catEngine.questionHistory;
  const testDuration = questionHistory.length > 0 
    ? new Date(questionHistory[questionHistory.length - 1].timestamp) - new Date(questionHistory[0].timestamp)
    : 0;
  
  // Calculer le temps de réponse moyen
  const responseTimes = questionHistory
    .map(entry => calculateResponseTime(entry, questionHistory))
    .filter(time => time > 0);
  const averageResponseTime = responseTimes.length > 0 
    ? responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length 
    : 0;

  // Construire les statistiques par niveau
  const levelStats = buildLevelStats(catEngine);
  
  // Construire la séquence des questions avec détails
  const questionSequence = questionHistory.map((entry, index) => ({
    questionId: entry.questionId,
    level: entry.level,
    isCorrect: entry.isCorrect,
    score: entry.score,
    responseTime: calculateResponseTime(entry, questionHistory, index),
    timestamp: new Date(entry.timestamp)
  }));

  return {
    // Performance finale de l'élève
    finalAbilityLevel: finalReport.finalLevel,
    finalLevel: finalReport.finalLevel,
    isFailureBasedAttribution: finalReport.completionReason === 'failure_threshold_reached',
    
    // Parcours de l'élève pendant le test
    totalQuestionsAnswered: finalReport.totalQuestionsAnswered,
    maxLevelReached,
    minLevelReached,
    
    // Raison de fin spécifique à cette session élève
    completionReason: finalReport.completionReason,

    // Séquence des questions répondues par l'élève
    questionSequence,

    // Progression de l'élève entre les niveaux
    levelProgression,

    // Performance de l'élève par niveau
    levelStats,

    // Métriques de performance de l'élève
    performance: {
      totalCorrect: finalReport.totalSuccesses,
      totalIncorrect: finalReport.totalFailures,
      globalSuccessRate: finalReport.successRate,
      averageResponseTime: Math.round(averageResponseTime / 1000 * 100) / 100, // en secondes
      testDuration: Math.round(testDuration / 1000 * 100) / 100 // en secondes
    }
  };
}

/**
 * Construit les statistiques par niveau
 * @param {Object} catEngine - Instance du moteur CAT
 * @returns {Object} Statistiques par niveau
 */
function buildLevelStats(catEngine) {
  const levelBreakdown = catEngine.generateLevelBreakdown();
  const levelStats = {};
  
  // Initialiser tous les niveaux possibles (1-5)
  for (let i = 1; i <= 5; i++) {
    levelStats[`level${i}`] = { attempted: 0, correct: 0, incorrect: 0 };
  }
  
  // Remplir avec les données réelles
  levelBreakdown.forEach(levelData => {
    levelStats[`level${levelData.level}`] = {
      attempted: levelData.questionsAttempted,
      correct: levelData.successes,
      incorrect: levelData.failures
    };
  });
  
  return levelStats;
}

/**
 * Calcule le temps de réponse pour une question
 * @param {Object} entry - Entrée de l'historique
 * @param {Array} questionHistory - Historique complet
 * @param {number} index - Index de la question
 * @returns {number} Temps de réponse en millisecondes
 */
function calculateResponseTime(entry, questionHistory, index) {
  if (index === undefined) {
    // Si index non fourni, le trouver
    index = questionHistory.findIndex(q => q.questionId === entry.questionId && q.timestamp === entry.timestamp);
  }
  
  if (index === 0) {
    // Première question, pas de temps de référence précédent
    return 0;
  }
  
  const currentTime = new Date(entry.timestamp);
  const previousTime = new Date(questionHistory[index - 1].timestamp);
  
  return currentTime - previousTime;
}

/**
 * Prépare les données CAT complètes pour la base de données
 * @param {Object} baseResultData - Données de base du résultat
 * @param {Object} catEngine - Instance du moteur CAT
 * @param {Object} responsesStore - Store des réponses
 * @returns {Object} Données complètes pour la base de données
 */
export function prepareCATResultForDatabase(baseResultData, catEngine, responsesStore) {
  if (!catEngine) {
    throw new Error('CAT Engine is required for CAT result preparation');
  }

  const catData = buildCATData(catEngine, responsesStore);
  
  return {
    ...baseResultData,
    testType: 'CAT',
    catData
  };
}

/**
 * Valide la structure des données CAT avant envoi
 * @param {Object} catData - Données CAT à valider
 * @returns {Object} Résultat de validation { isValid: boolean, errors: string[] }
 */
export function validateCATData(catData) {
  const errors = [];
  
  // Vérifications obligatoires
  if (typeof catData.finalAbilityLevel !== 'number') {
    errors.push('finalAbilityLevel must be a number');
  }
  
  if (typeof catData.finalLevel !== 'number') {
    errors.push('finalLevel must be a number');
  }
  
  if (typeof catData.totalQuestionsAnswered !== 'number') {
    errors.push('totalQuestionsAnswered must be a number');
  }
  
  if (!Array.isArray(catData.questionSequence)) {
    errors.push('questionSequence must be an array');
  }
  
  if (!Array.isArray(catData.levelProgression)) {
    errors.push('levelProgression must be an array');
  }
  
  if (!catData.performance || typeof catData.performance !== 'object') {
    errors.push('performance object is required');
  }
  
  // Vérifications de cohérence
  if (catData.questionSequence && catData.totalQuestionsAnswered) {
    if (catData.questionSequence.length !== catData.totalQuestionsAnswered) {
      errors.push('questionSequence length must match totalQuestionsAnswered');
    }
  }
  
  if (catData.maxLevelReached && catData.minLevelReached) {
    if (catData.maxLevelReached < catData.minLevelReached) {
      errors.push('maxLevelReached must be >= minLevelReached');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Utilitaire pour logger les données CAT (debug)
 * @param {Object} catData - Données CAT
 * @param {string} context - Contexte du log
 */
export function logCATData(catData, context = 'CAT Data') {
  console.group(`📊 ${context}`);
  console.log('📈 Performance finale:', {
    finalAbilityLevel: catData.finalAbilityLevel,
    finalLevel: catData.finalLevel,
    isFailureBasedAttribution: catData.isFailureBasedAttribution
  });
  console.log('🎯 Parcours:', {
    totalQuestionsAnswered: catData.totalQuestionsAnswered,
    maxLevelReached: catData.maxLevelReached,
    minLevelReached: catData.minLevelReached,
    completionReason: catData.completionReason
  });
  console.log('📊 Performance globale:', catData.performance);
  console.table(catData.levelStats);
  console.groupEnd();
}

export default {
  buildCATData,
  prepareCATResultForDatabase,
  validateCATData,
  logCATData
};